import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import type { FormEvent, ReactNode } from 'react';

import {
  addDressCode,
  addRole,
  answerShift,
  crewQuery,
  cancelShift,
  clashBlocks,
  clashLabel,
  createShift,
  dressCodesQuery,
  jobShiftsQuery,
  MISSING_TO_CONFIRM,
  offerOptionsQuery,
  offerShift,
  confirmShifts,
  releaseShift,
  placeLabel,
  removeShift,
  rolesQuery,
  SHIFT_STATE,
  updateShift,
  withdrawOffer,
  type Shift,
  type ShiftInput,
} from '../api/crew.ts';
import { gapsLabel } from '../api/compliance.ts';
import { createOffer, createSeveral, type Refused } from '../api/offers.ts';
import { isTimeOffClash, isTimeOffReason } from '../api/time-off.ts';
import { toSignLabel } from '../api/safety.ts';
import { stockLocationsQuery } from '../api/inventory.ts';
import { jobsQuery, type Job } from '../api/jobs.ts';
import { venuesQuery } from '../api/venues.ts';
import { localLabel } from '../api/quotes.ts';
import { Button } from '../components/button.tsx';
import { CrewPicker } from '../components/crew-picker.tsx';
import { meQuery } from '../api/me.ts';
import type { WorkContext } from '../api/abilities.ts';
import { EditButton, RemoveButton } from '../components/action-buttons.tsx';
import { Field } from '../components/field.tsx';
import { TextArea } from '../components/text-area.tsx';
import { dollarsToMinorUnits, formatMinorUnits, minorUnitsToDollars } from '../money.ts';

// CB-REQ CRW-11 – CRW-13 at v1.960 – v1.961, crew slice part 1: who works a job. Shifts are planned
// as drafts — open, or with someone in mind — published, offered as the office phones, and answered
// as the office hears back. A clash with another shift of the same person is shown in place; only a
// second accepted shift is refused, by the database (CRW-12). From crew part 4A (CRW-11 at v1.996)
// a shift is someone's booked time: where they report, the job it was booked for and who they ask;
// the work is its tasks (tasks-panel.tsx). When a job is cancelled its shifts stay, flagged, until
// each is booked for another job, recorded as pulled out, or cancelled (JOB-10 at v1.962). From
// crew part 4B (OPS-03 at v1.998) the row and form are hosted by the roster's popover; the job page
// shows its crew in summary (crew-summary.tsx).

const BASIS = { hourly: 'per hour', daily: 'per day', flat: 'flat' } as const;

type Opening = 'edit' | 'offer' | 'decline' | 'cancel' | 'release' | null;

export function ShiftRow({
  shift,
  job,
  editable,
  resolvable,
  seesPrices,
  onChanged,
}: {
  shift: Shift;
  /** The job whose page this is, if any: its lead is marked, and the shift is "for" it. */
  job?: Job | undefined;
  editable: boolean;
  /** The job was cancelled: only rebooking, pulled out and cancel remain (JOB-10 at v1.962). */
  resolvable: boolean;
  seesPrices: boolean;
  onChanged: () => Promise<void>;
}) {
  const [open, setOpen] = useState<Opening>(null);
  const [reason, setReason] = useState('');
  const act = useMutation({
    mutationFn: (run: () => Promise<unknown>) => run(),
    onSuccess: async () => {
      setOpen(null);
      await onChanged();
    },
  });
  const cancelled = shift.state === 'cancelled';
  const s = shift.state;

  if (open === 'edit') {
    return (
      <li>
        <ShiftForm job={job} shift={shift} seesPrices={seesPrices} onDone={() => setOpen(null)} />
      </li>
    );
  }

  return (
    <li className={`panel flex flex-col gap-2 p-4 ${cancelled ? 'opacity-60' : ''}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <p className="font-semibold">
            {shift.role?.name ?? (shift.held ? 'Held day' : 'No role')}
            {job?.leadCrewMemberId && shift.crewMember?.id === job.leadCrewMemberId ? (
              <span className="tag tag-accent ml-2">Lead</span>
            ) : null}
            <span className="mx-2 text-muted">·</span>
            {/* CRW-11 at v1.994: a shift is a booking; one with no one is not a gap to count. */}
            {shift.crewMember ? shift.crewMember.name : <span className="tag">No one yet</span>}
          </p>
          <p className="text-sm text-ink-soft">
            {placeLabel(shift)} ·{' '}
            {shift.bookedForJob && shift.bookedForJob.id !== job?.id
              ? `for J-${shift.bookedForJob.number} · `
              : ''}
            {localLabel(shift.window.localStart)} – {shift.window.localEnd.slice(11)}
            {shift.localCallAt !== shift.window.localStart
              ? ` · call ${shift.localCallAt.slice(11)}`
              : ''}
            {shift.meetingPoint ? ` · meet at ${shift.meetingPoint}` : ''}
            {shift.directToSite ? ' · straight to site' : ''}
            {shift.directFromSite ? ' · home from site' : ''}
            {shift.dressCode ? ` · ${shift.dressCode.name}` : ''}
            {shift.contact ? ` · ask ${shift.contact.name}` : ''}
          </p>
          {/* OPS-02 at v1.996: the work it carries. */}
          {shift.tasks.length > 0 ? (
            <p className="flex flex-wrap gap-1.5 text-sm">
              {shift.tasks.map((t) => (
                <span key={t.id} className={`tag ${t.fit === 'fits' ? '' : 'tag-accent-2'}`}>
                  {t.description}
                </span>
              ))}
            </p>
          ) : null}
          {seesPrices && shift.rate ? (
            <p className="text-sm text-ink-soft">
              {formatMinorUnits(shift.rate.amountMinor, shift.rate.currency)}{' '}
              {BASIS[shift.rate.basis]}
            </p>
          ) : null}
          {shift.crewNotes ? <p className="text-sm">For crew: {shift.crewNotes}</p> : null}
          {shift.officeNotes ? (
            <p className="text-sm text-ink-soft">Office: {shift.officeNotes}</p>
          ) : null}
          {cancelled && shift.cancelReason ? (
            <p className="text-sm text-ink-soft">Released: {shift.cancelReason}</p>
          ) : null}
          {shift.state === 'declined' && shift.declineReason ? (
            <p className="text-sm text-ink-soft">Declined: {shift.declineReason}</p>
          ) : null}
          {/* CMP-07 at v1.965: what they lack for this shift, from the venue, role or job. */}
          {!cancelled && shift.complianceGaps.length > 0 ? (
            <p className="text-sm">
              <span className="tag tag-accent-2">Needs {gapsLabel(shift.complianceGaps)}</span>
            </p>
          ) : null}
          {/* CMP-02 / CMP-04 at v1.967: not yet signed — flagged, never refused. */}
          {!cancelled && shift.safetyGaps.length > 0 ? (
            <p className="text-sm">
              <span className="tag tag-accent">To sign: {toSignLabel(shift.safetyGaps)}</span>
            </p>
          ) : null}
          {!cancelled && shift.clashes.length > 0 ? (
            <p className="flex flex-wrap items-center gap-1.5 text-sm">
              <span className="text-ink-soft">Also on</span>
              {shift.clashes.map((c) =>
                c.bookedForJob ? (
                  <Link
                    key={c.shiftId}
                    to="/jobs/$jobId"
                    params={{ jobId: c.bookedForJob.id }}
                    className={`tag ${clashBlocks(c) && s !== 'accepted' ? 'tag-accent-2' : ''}`}
                  >
                    {clashLabel(c)}
                  </Link>
                ) : (
                  <span
                    key={c.shiftId}
                    className={`tag ${clashBlocks(c) && s !== 'accepted' ? 'tag-accent-2' : ''}`}
                  >
                    {clashLabel(c)}
                  </span>
                ),
              )}
            </p>
          ) : null}
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className={`tag ${s === 'accepted' ? 'tag-accent' : ''}`}>{SHIFT_STATE[s]}</span>
          {/* CRW-11 at v1.996: booked for the day before where they report is known. */}
          {shift.held && shift.lapsed ? (
            <span className="tag">Lapsed — no place given</span>
          ) : shift.held && !cancelled ? (
            <span className={`tag ${shift.toAssign ? 'tag-accent-2' : ''}`}>
              {shift.toAssign ? 'Held — place it now' : 'Held'}
            </span>
          ) : null}
          {shift.bookedForJob?.cancelled && !cancelled ? (
            <span className="tag tag-accent-2">Job cancelled</span>
          ) : null}
          {/* CRW-11 at v1.997: booked for the job, on none of its tasks. */}
          {shift.untasked && !cancelled ? <span className="tag">No task</span> : null}
          {/* CRW-16 at v1.996: what confirming needs; on a confirmed shift, the flag. */}
          {!cancelled &&
          shift.missingToConfirm.length > 0 &&
          (s === 'accepted' || shift.confirmedAt) ? (
            <span className="tag tag-accent-2">
              {shift.confirmedAt ? 'Missing' : 'Needs'}{' '}
              {shift.missingToConfirm.map((m) => MISSING_TO_CONFIRM[m]).join(', ')}
            </span>
          ) : null}
          {s === 'accepted' && !shift.confirmedAt ? (
            <span className={`tag ${shift.toConfirm ? 'tag-accent-2' : ''}`}>
              {shift.toConfirm ? 'To confirm now' : 'Not confirmed yet'}
            </span>
          ) : null}
          {s === 'accepted' && shift.confirmedAt && shift.acknowledgedAt ? (
            <span className="tag">Confirmed · they’ve read it</span>
          ) : null}
          {/* CRW-14 at v1.975: told of a change, and not yet tapped Got it. */}
          {s === 'accepted' && shift.changeOutcome === 'notify' ? (
            <span className="tag">Told of a change — not yet seen</span>
          ) : null}
          {s === 'accepted' && shift.confirmedAt && !shift.acknowledgedAt ? (
            <span className="tag">
              {shift.changeOutcome === 'reconfirm'
                ? 'Changed — for them to read again'
                : 'Confirmed · not yet read by them'}
            </span>
          ) : null}
          {s === 'offered' && shift.changeOutcome === 'reaccept' ? (
            <span className="tag">Changed — to accept again</span>
          ) : null}
        </div>
      </div>

      {resolvable && !cancelled ? (
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setOpen('edit')}>Rebook…</Button>
          {s === 'offered' || s === 'accepted' ? (
            <Button variant="quiet" onClick={() => setOpen('decline')}>
              Pulled out…
            </Button>
          ) : null}
          <Button variant="quiet" onClick={() => setOpen('cancel')}>
            Cancel…
          </Button>
        </div>
      ) : null}

      {editable && !cancelled ? (
        <div className="flex flex-wrap gap-2">
          {s === 'draft' || s === 'published' || s === 'declined' ? (
            <Button
              variant={s === 'declined' ? 'quiet' : 'primary'}
              onClick={() => setOpen('offer')}
            >
              {s === 'declined' ? 'Re-offer…' : 'Offer…'}
            </Button>
          ) : null}
          {s === 'offered' ? (
            <>
              <Button
                disabled={act.isPending}
                onClick={() => act.mutate(() => answerShift(shift.id, { answer: 'accepted' }))}
              >
                Mark accepted
              </Button>
              <Button variant="quiet" onClick={() => setOpen('decline')}>
                Mark declined…
              </Button>
              <Button
                variant="quiet"
                disabled={act.isPending}
                onClick={() => act.mutate(() => withdrawOffer(shift.id))}
              >
                Withdraw
              </Button>
            </>
          ) : null}
          {s === 'accepted' && !shift.confirmedAt && shift.missingToConfirm.length === 0 ? (
            <Button
              disabled={act.isPending}
              onClick={() => act.mutate(() => confirmShifts([shift.id]))}
            >
              Confirm
            </Button>
          ) : null}
          {s === 'accepted' ? (
            <Button variant="quiet" onClick={() => setOpen('decline')}>
              Pulled out…
            </Button>
          ) : null}
          {/* CRW-11 at v1.996: a held day is given its place on the shift's own form. */}
          {shift.held && !shift.lapsed ? (
            <Button variant={shift.toAssign ? 'primary' : 'quiet'} onClick={() => setOpen('edit')}>
              Place…
            </Button>
          ) : null}
          <EditButton what={shiftName(shift)} onClick={() => setOpen('edit')} />
          {s === 'draft' ? (
            <RemoveButton
              what={shiftName(shift)}
              disabled={act.isPending}
              onClick={() => act.mutate(() => removeShift(shift.id))}
            />
          ) : shift.held ? (
            <Button variant="quiet" onClick={() => setOpen('release')}>
              Release…
            </Button>
          ) : (
            <Button variant="quiet" onClick={() => setOpen('cancel')}>
              Cancel…
            </Button>
          )}
        </div>
      ) : null}

      {/* CRW-11 at v1.980: a held day released, with the reason the person sees. */}
      {open === 'release' ? (
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-56 flex-1">
            <Field
              label={`Why it’s released (${shift.crewMember?.name ?? 'they'} will see this)`}
              value={reason}
              onChange={(e) => setReason(e.currentTarget.value)}
            />
          </div>
          <Button
            disabled={act.isPending || !reason.trim()}
            onClick={() => act.mutate(() => releaseShift(shift.id, reason.trim()))}
          >
            Yes, release
          </Button>
          <Button variant="quiet" onClick={() => setOpen(null)}>
            Back
          </Button>
        </div>
      ) : null}
      {open === 'offer' ? (
        <OfferPicker
          shift={shift}
          onOffer={(crewMemberId) => act.mutate(() => offerShift(shift.id, crewMemberId))}
          onClose={() => setOpen(null)}
          busy={act.isPending}
        />
      ) : null}
      {open === 'decline' ? (
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-56 flex-1">
            <Field
              label="Why (optional)"
              value={reason}
              onChange={(e) => setReason(e.currentTarget.value)}
            />
          </div>
          <Button
            disabled={act.isPending}
            onClick={() =>
              act.mutate(() =>
                answerShift(shift.id, { answer: 'declined', reason: reason.trim() || null }),
              )
            }
          >
            Save
          </Button>
          <Button variant="quiet" onClick={() => setOpen(null)}>
            Back
          </Button>
        </div>
      ) : null}
      {open === 'cancel' ? (
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm">
            {shift.crewMember && s !== 'declined'
              ? `Cancel this shift? Tell ${shift.crewMember.name} yourself — AVenue sends nothing yet.`
              : 'Cancel this shift? It stays here as cancelled.'}
          </p>
          <Button disabled={act.isPending} onClick={() => act.mutate(() => cancelShift(shift.id))}>
            Yes, cancel it
          </Button>
          <Button variant="quiet" onClick={() => setOpen(null)}>
            Back
          </Button>
        </div>
      ) : null}
      {act.isError ? (
        <p role="alert" className="alert">
          {act.error.message}
        </p>
      ) : null}
    </li>
  );
}

/** Each crew member, with what offering them this shift would clash with (CRW-12). */
export function OfferPicker({
  shift,
  onOffer,
  onClose,
  busy,
}: {
  shift: Shift;
  onOffer: (crewMemberId: string) => void;
  onClose: () => void;
  busy: boolean;
}) {
  // CMP-02 at v1.967: what each would have to sign, which the eligible read doesn't carry.
  const options = useQuery(offerOptionsQuery(shift.id));
  const toSign = (id: string) => {
    const gaps = options.data?.find((o) => o.crewMember.id === id)?.safetyGaps ?? [];
    return gaps.length > 0 ? (
      <span className="tag tag-accent">To sign: {toSignLabel(gaps)}</span>
    ) : null;
  };
  return (
    <div className="flex flex-col gap-2 rounded-md bg-tile p-3">
      <p className="text-sm font-semibold">Offer this shift to…</p>
      {/* CRW-03 at v1.9107 (crew part 4E-3): who could do it, filtered from the shift. */}
      <CrewPicker
        work={{
          start: shift.window.localStart.slice(0, 16),
          end: shift.window.localEnd.slice(0, 16),
          venueId: shift.venue?.id,
          locationId: shift.location?.id,
          roleId: shift.role?.id,
          jobId: shift.bookedForJob?.id,
          excludeShiftId: shift.id,
        }}
        zone={shift.window.zone}
        roleName={shift.role?.name ?? null}
        disabled={busy}
        onChoose={onOffer}
        extra={toSign}
      />
      <Button variant="quiet" className="self-start" onClick={onClose}>
        Back
      </Button>
    </div>
  );
}

/** Wall-clock windows the form can fill from: the job's phases, or its whole window. */
function phases(job: Job): Array<{ label: string; start: string; end: string }> {
  if (job.eventWindows) {
    return [
      { label: 'Bump-in', ...local(job.eventWindows.bumpIn) },
      { label: 'Event', ...local(job.eventWindows.event) },
      { label: 'Bump-out', ...local(job.eventWindows.bumpOut) },
    ];
  }
  return [{ label: 'The whole job', ...local(job.scheduledWindow) }];
}

function local(w: { localStart: string; localEnd: string }) {
  return { start: w.localStart.slice(0, 16), end: w.localEnd.slice(0, 16) };
}

/** Where a shift reports, as the form's one select holds it: a venue, the depot, or held. */
const HELD = 'held';
const placeOf = (s: Pick<Shift, 'venue' | 'location'>) =>
  s.venue ? `venue:${s.venue.id}` : s.location ? `location:${s.location.id}` : HELD;

export function ShiftForm({
  job,
  date,
  shift,
  seesPrices,
  asOfferFirst = false,
  bare = false,
  formId,
  drawn,
  onDone,
}: {
  /** The job whose page this is: a new shift is booked for it, at its venue, unless changed. */
  job?: Job | undefined;
  /** For a new shift off a job's page: the local date it defaults to. */
  date?: string | undefined;
  shift?: Shift;
  seesPrices: boolean;
  /** The roster's + Offer: a new shift starts as an offer to the first who accept (CRW-04). */
  asOfferFirst?: boolean;
  /** In the roster's popover: no panel; an existing shift's Save is in the popover's foot. */
  bare?: boolean;
  /** So a button outside the form (the popover's foot) can submit it. */
  formId?: string;
  /** OPS-03 at v1.9105: a window drawn on the roster, with the person and job it was drawn for. */
  drawn?:
    | { start: string; end: string; crewMemberId?: string | undefined; jobId?: string | undefined }
    | undefined;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const roles = useQuery(rolesQuery);
  const me = useQuery(meQuery);
  const dressCodes = useQuery(dressCodesQuery);
  const locations = useQuery(stockLocationsQuery);
  const venues = useQuery(venuesQuery);
  const jobs = useQuery(jobsQuery);
  const contacts = useQuery(crewQuery(false));
  // CRW-11 at v1.996: where they report — a venue, the depot, or held (no place yet).
  const [place, setPlace] = useState(
    shift ? placeOf(shift) : job ? (job.venue ? `venue:${job.venue.id}` : '') : '',
  );
  const held = place === HELD;
  const [bookedForJobId, setBookedForJobId] = useState(
    shift ? (shift.bookedForJob?.id ?? '') : (job?.id ?? drawn?.jobId ?? ''),
  );
  const [contactId, setContactId] = useState(shift?.contact?.id ?? '');
  const depot = (locations.data ?? []).find((l) => l.kind === 'depot');
  const day = date ?? shift?.window.localStart.slice(0, 10) ?? '';
  const first = job ? phases(job)[0]! : { start: `${day}T08:00`, end: `${day}T16:00` };
  const [roleId, setRoleId] = useState(shift?.role?.id ?? '');
  const [newRole, setNewRole] = useState<string | null>(null);
  const [start, setStart] = useState(shift?.window.localStart ?? drawn?.start ?? first.start);
  const [end, setEnd] = useState(shift?.window.localEnd ?? drawn?.end ?? first.end);
  const [callAt, setCallAt] = useState(
    shift && shift.localCallAt !== shift.window.localStart ? shift.localCallAt : '',
  );
  const [meetingPoint, setMeetingPoint] = useState(shift?.meetingPoint ?? '');
  const [directToSite, setDirectToSite] = useState(shift?.directToSite ?? false);
  const [directFromSite, setDirectFromSite] = useState(shift?.directFromSite ?? false);
  const [dressCodeId, setDressCodeId] = useState(shift?.dressCode?.id ?? '');
  const [newDressCode, setNewDressCode] = useState<string | null>(null);
  const [crewNotes, setCrewNotes] = useState(shift?.crewNotes ?? '');
  const [officeNotes, setOfficeNotes] = useState(shift?.officeNotes ?? '');
  const [basis, setBasis] = useState<'hourly' | 'daily' | 'flat'>(shift?.rate?.basis ?? 'hourly');
  const [amount, setAmount] = useState(
    shift?.rate ? minorUnitsToDollars(shift.rate.amountMinor) : '',
  );
  const [problem, setProblem] = useState<string | undefined>();
  // CRW-11 and CRW-04 at v1.994: several people at once — a shift each, or one offer whose first
  // to accept take its places.
  const crew = contacts;
  const [people, setPeople] = useState<string[]>(drawn?.crewMemberId ? [drawn.crewMemberId] : []);
  const [offerNow, setOfferNow] = useState(true);
  const [firstToAccept, setFirstToAccept] = useState(asOfferFirst);
  const [places, setPlaces] = useState('1');
  const [refused, setRefused] = useState<Refused[]>([]);
  // CRW-02 at v1.9106: the last save, to send again through someone's time off.
  const [sent, setSent] = useState<ShiftInput | null>(null);
  const asOffer = firstToAccept && people.length > 0;
  // Live jobs to book it for: not cancelled, not over — and the one it is already booked for.
  const bookable = (jobs.data ?? []).filter(
    (j) => j.status === 'confirmed' || j.status === 'out' || j.id === bookedForJobId,
  );

  const save = useMutation({
    mutationFn: async (input: ShiftInput): Promise<Refused[]> => {
      // A role or dress code named here is added to the tenant's list first (CRW-11 at v1.961).
      const role = newRole !== null ? (await addRole(newRole.trim())).id : input.roleId || null;
      const dress =
        newDressCode !== null ? (await addDressCode(newDressCode.trim())).id : input.dressCodeId;
      const body = { ...input, roleId: role, dressCodeId: dress ?? null };
      // Kept as sent, the new role and dress code resolved, to send again through time off; and
      // chosen now, so sending again doesn't add them twice.
      setSent(body);
      if (newRole !== null && role) {
        setNewRole(null);
        setRoleId(role);
      }
      if (newDressCode !== null && dress) {
        setNewDressCode(null);
        setDressCodeId(dress);
      }
      if (shift) {
        await updateShift(shift.id, body);
        return [];
      }
      if (asOffer) {
        return (await createOffer({ ...body, places: Number(places) }, people)).refused;
      }
      if (people.length > 0) return (await createSeveral(body, people, offerNow)).refused;
      await createShift(body);
      return [];
    },
    onSuccess: async (notOffered) => {
      // Whoever could not be booked or offered it is named at once; the rest stand.
      if (notOffered.length > 0) setRefused(notOffered);
      if (job) {
        await queryClient.invalidateQueries({ queryKey: jobShiftsQuery(job.id).queryKey });
        await queryClient.invalidateQueries({ queryKey: ['jobs', job.id, 'offers'] });
        await queryClient.invalidateQueries({ queryKey: ['jobs', job.id, 'tasks'] });
      }
      await queryClient.invalidateQueries({ queryKey: ['shifts'] });
      await queryClient.invalidateQueries({ queryKey: ['shift-offers'] });
      await queryClient.invalidateQueries({ queryKey: rolesQuery.queryKey });
      await queryClient.invalidateQueries({ queryKey: dressCodesQuery.queryKey });
      await queryClient.invalidateQueries({ queryKey: ['roster'] });
      if (notOffered.length === 0) onDone();
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!place) return setProblem('Choose where: a venue, the depot, or held.');
    // CRW-11 at v1.996: a role is never required; a new one needs its name.
    if (newRole !== null && !newRole.trim()) return setProblem('Give the new role a name.');
    if (asOffer && !(Number(places) >= 1 && Number.isInteger(Number(places)))) {
      return setProblem('Places: a whole number, 1 or more.');
    }
    if (!start || !end || end <= start) return setProblem('The shift ends after it starts.');
    if (callAt && callAt > start) return setProblem('The call time is no later than the start.');
    let rate: ShiftInput['rate'];
    if (seesPrices) {
      if (amount.trim()) {
        const minor = dollarsToMinorUnits(amount);
        if (minor === null)
          return setProblem('The rate is an amount in dollars, e.g. 55 or 55.50.');
        rate = { basis, amountMinor: minor };
      } else {
        rate = null;
      }
    }
    setProblem(undefined);
    const [kind, placeId] = place.split(':') as ['venue' | 'location' | typeof HELD, string];
    send({
      venueId: kind === 'venue' ? placeId : null,
      locationId: kind === 'location' ? placeId : null,
      bookedForJobId: bookedForJobId || null,
      contactCrewMemberId: contactId || null,
      roleId,
      window: { start, end },
      callAt: callAt || null,
      meetingPoint: meetingPoint.trim() || null,
      directToSite,
      directFromSite,
      dressCodeId: dressCodeId || null,
      crewNotes: crewNotes.trim() || null,
      officeNotes: officeNotes.trim() || null,
      ...(rate === undefined ? {} : { rate }),
    });
  }

  // CRW-02 at v1.9106: someone left out for their time off is booked anyway, one at a time.
  const anyway = useMutation({
    mutationFn: async (crewMemberId: string) => {
      if (!sent) return crewMemberId;
      const made = await createShift({ ...sent, crewMemberId, throughTimeOff: {} });
      if (offerNow) await offerShift(made.id, crewMemberId, {});
      return crewMemberId;
    },
    onSuccess: async (crewMemberId) => {
      const rest = refused.filter((r) => r.crewMemberId !== crewMemberId);
      setRefused(rest);
      await queryClient.invalidateQueries({ queryKey: ['roster'] });
      await queryClient.invalidateQueries({ queryKey: ['shifts'] });
      if (rest.length === 0) onDone();
    },
  });

  function send(input: ShiftInput) {
    save.mutate(input);
  }
  const clash = isTimeOffClash(save.error) ? save.error : null;

  const select = (id: string, label: string, control: ReactNode, below?: ReactNode) => (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-xs text-ink-soft">
        {label}
      </label>
      {control}
      {below}
    </div>
  );

  // Laid out as the operator's roster design draws a shift (2026-10-26): a two-column grid of its
  // fields, then the notes; on the roster's popover its Save is in the popover's foot.
  return (
    <form
      id={formId}
      onSubmit={submit}
      className={bare ? 'flex flex-col gap-4' : 'panel flex flex-col gap-4'}
      aria-label="Shift"
    >
      {!shift ? (
        <People
          work={
            start && end && end > start && place
              ? {
                  start: start.slice(0, 16),
                  end: end.slice(0, 16),
                  venueId: place.startsWith('venue:') ? place.slice(6) : null,
                  locationId: place.startsWith('location:') ? place.slice(9) : null,
                  roleId: roleId || null,
                  jobId: bookedForJobId || null,
                }
              : null
          }
          zone={
            (place.startsWith('venue:')
              ? venues.data?.find((v) => v.id === place.slice(6))?.timeZone
              : null) ??
            me.data?.tenant.defaultTz ??
            'Australia/Melbourne'
          }
          roleName={roles.data?.find((r) => r.id === roleId)?.name ?? null}
          crew={crew.data ?? []}
          chosen={people}
          onChange={setPeople}
          offerNow={offerNow}
          onOfferNow={setOfferNow}
          firstToAccept={firstToAccept}
          onFirstToAccept={setFirstToAccept}
          places={places}
          onPlaces={setPlaces}
        />
      ) : null}

      {job ? (
        <div className="flex flex-wrap gap-2">
          {phases(job).map((p) => (
            <Button
              key={p.label}
              type="button"
              variant="quiet"
              onClick={() => {
                setStart(p.start);
                setEnd(p.end);
              }}
            >
              {p.label}
            </Button>
          ))}
        </div>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2">
        {select(
          'shift-role',
          'Role',
          <select
            id="shift-role"
            className="input"
            value={newRole !== null ? '+' : roleId}
            onChange={(e) => {
              const v = e.currentTarget.value;
              if (v === '+') return setNewRole('');
              setNewRole(null);
              setRoleId(v);
            }}
          >
            <option value="">None</option>
            {(roles.data ?? []).map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
            <option value="+">+ New role</option>
          </select>,
          newRole !== null ? (
            <Field
              label="New role’s name"
              value={newRole}
              onChange={(e) => setNewRole(e.currentTarget.value)}
            />
          ) : null,
        )}
        {select(
          'shift-booked-for',
          'Booked for',
          <select
            id="shift-booked-for"
            className="input"
            value={bookedForJobId}
            onChange={(e) => setBookedForJobId(e.currentTarget.value)}
          >
            <option value="">No job</option>
            {bookable.map((j) => (
              <option key={j.id} value={j.id}>
                {j.reference} — {j.name}
              </option>
            ))}
          </select>,
        )}
        <Field
          label="Starts"
          type="datetime-local"
          value={start}
          onChange={(e) => setStart(e.currentTarget.value)}
        />
        <Field
          label="Ends"
          type="datetime-local"
          value={end}
          onChange={(e) => setEnd(e.currentTarget.value)}
        />
        <Field
          label="Call time"
          hint="If earlier than the start."
          type="datetime-local"
          value={callAt}
          onChange={(e) => setCallAt(e.currentTarget.value)}
        />
        {select(
          'shift-place',
          'Where',
          <select
            id="shift-place"
            className="input"
            value={place}
            onChange={(e) => setPlace(e.currentTarget.value)}
          >
            <option value="">Choose…</option>
            {(venues.data ?? []).map((v) => (
              <option key={v.id} value={`venue:${v.id}`}>
                {v.name}
              </option>
            ))}
            {depot ? <option value={`location:${depot.id}`}>{depot.name}</option> : null}
            <option value={HELD}>Venue to come</option>
          </select>,
          held ? (
            <p className="text-xs text-ink-soft">Held: say where before you confirm it.</p>
          ) : null,
        )}
        {select(
          'shift-dress',
          'Dress code',
          <select
            id="shift-dress"
            className="input"
            value={newDressCode !== null ? '+' : dressCodeId}
            onChange={(e) => {
              const v = e.currentTarget.value;
              if (v === '+') return setNewDressCode('');
              setNewDressCode(null);
              setDressCodeId(v);
            }}
          >
            <option value="">None yet</option>
            {(dressCodes.data ?? []).map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
            <option value="+">+ New dress code</option>
          </select>,
          newDressCode !== null ? (
            <Field
              label="New dress code"
              value={newDressCode}
              onChange={(e) => setNewDressCode(e.currentTarget.value)}
            />
          ) : null,
        )}
        {/* CRW-16 at v1.996: who they ask on the day; their number is shown to them. */}
        {select(
          'shift-contact',
          'Contact',
          <select
            id="shift-contact"
            className="input"
            value={contactId}
            onChange={(e) => setContactId(e.currentTarget.value)}
          >
            <option value="">None yet</option>
            {(contacts.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.displayName}
              </option>
            ))}
          </select>,
        )}
        <Field
          label="Meeting point"
          value={meetingPoint}
          onChange={(e) => setMeetingPoint(e.currentTarget.value)}
        />
        {seesPrices ? (
          <div className="grid grid-cols-[1fr_7rem] gap-2">
            <Field
              label="Agreed rate ($)"
              hint="Only staff who can see prices see this."
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.currentTarget.value)}
            />
            {select(
              'shift-basis',
              'Rate is',
              <select
                id="shift-basis"
                className="input"
                value={basis}
                onChange={(e) => setBasis(e.currentTarget.value as typeof basis)}
              >
                <option value="hourly">Hourly</option>
                <option value="daily">Daily</option>
                <option value="flat">Flat</option>
              </select>,
            )}
          </div>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={directToSite}
            onChange={(e) => setDirectToSite(e.currentTarget.checked)}
          />
          Direct to site
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={directFromSite}
            onChange={(e) => setDirectFromSite(e.currentTarget.checked)}
          />
          Home from site
        </label>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <TextArea
          label="Crew notes"
          hint="They see these once they sign in."
          rows={2}
          value={crewNotes}
          onChange={(e) => setCrewNotes(e.currentTarget.value)}
        />
        <TextArea
          label="Office notes"
          hint="Never shown to crew."
          rows={2}
          value={officeNotes}
          onChange={(e) => setOfficeNotes(e.currentTarget.value)}
        />
      </div>

      {clash && sent && !problem ? (
        <div role="alert" className="notice flex flex-wrap items-center gap-2 text-sm">
          <span className="min-w-0 flex-1">{clash.message}</span>
          <Button
            type="button"
            className="small-btn"
            disabled={save.isPending}
            onClick={() => save.mutate({ ...sent, throughTimeOff: {} })}
          >
            Book anyway
          </Button>
        </div>
      ) : problem || save.isError ? (
        <p role="alert" className="alert">
          {problem ?? save.error?.message}
        </p>
      ) : null}
      {refused.length > 0 ? (
        <div role="alert" className="alert flex flex-col gap-1">
          <p className="font-semibold">Not booked</p>
          <ul className="text-sm">
            {refused.map((r) => (
              <li key={r.crewMemberId}>
                {crew.data?.find((c) => c.id === r.crewMemberId)?.displayName ?? 'Someone'}:{' '}
                {r.reason}
                {isTimeOffReason(r.reason) && !asOffer ? (
                  <Button
                    type="button"
                    variant="quiet"
                    className="small-btn ml-2"
                    disabled={anyway.isPending}
                    onClick={() => anyway.mutate(r.crewMemberId)}
                  >
                    Book anyway
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {bare && shift ? null : (
        <div className="flex gap-2">
          {refused.length > 0 ? (
            <Button type="button" onClick={onDone}>
              Done
            </Button>
          ) : (
            <Button type="submit" disabled={save.isPending}>
              {shift ? 'Save' : asOffer ? 'Offer' : people.length > 0 && offerNow ? 'Offer' : 'Add'}
            </Button>
          )}
          <Button type="button" variant="quiet" onClick={onDone}>
            Cancel
          </Button>
        </div>
      )}
    </form>
  );
}

/**
 * CRW-11 and CRW-04 at v1.994: who a new shift is for. None is an open shift; several make a shift
 * each, offered now or left as drafts — or, first to accept, one offer for a number of places.
 */
function People({
  work,
  zone,
  roleName,
  crew,
  chosen,
  onChange,
  offerNow,
  onOfferNow,
  firstToAccept,
  onFirstToAccept,
  places,
  onPlaces,
}: {
  /** CRW-03 at v1.9107: the shift as entered so far; null until it has times and a place. */
  work: WorkContext | null;
  zone: string;
  roleName: string | null;
  crew: ReadonlyArray<{ id: string; displayName: string }>;
  chosen: string[];
  onChange: (ids: string[]) => void;
  offerNow: boolean;
  onOfferNow: (on: boolean) => void;
  firstToAccept: boolean;
  onFirstToAccept: (on: boolean) => void;
  places: string;
  onPlaces: (places: string) => void;
}) {
  const toggle = (id: string) =>
    onChange(chosen.includes(id) ? chosen.filter((c) => c !== id) : [...chosen, id]);
  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="text-xs text-ink-soft">Who</legend>
      {work ? (
        // CRW-03 at v1.9107 (crew part 4E-3): who could do it, filtered from the shift so far.
        <div className="rounded-md bg-tile p-2">
          <CrewPicker
            work={work}
            zone={zone}
            roleName={roleName}
            chosen={chosen}
            onChoose={toggle}
          />
        </div>
      ) : (
        <ul className="flex max-h-48 flex-col gap-1 overflow-y-auto rounded-md bg-tile p-2 text-sm">
          {crew.map((c) => (
            <li key={c.id}>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={chosen.includes(c.id)}
                  onChange={() => toggle(c.id)}
                />
                {c.displayName}
              </label>
            </li>
          ))}
        </ul>
      )}
      {chosen.length > 0 ? (
        <div className="flex flex-wrap items-end gap-x-6 gap-y-2 text-sm">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={firstToAccept}
              onChange={(e) => onFirstToAccept(e.currentTarget.checked)}
            />
            First to accept
          </label>
          {firstToAccept ? (
            <div className="w-24">
              <Field
                label="Places"
                inputMode="numeric"
                value={places}
                onChange={(e) => onPlaces(e.currentTarget.value)}
              />
            </div>
          ) : (
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={offerNow}
                onChange={(e) => onOfferNow(e.currentTarget.checked)}
              />
              Offer now
            </label>
          )}
        </div>
      ) : null}
    </fieldset>
  );
}

/** A shift's name for screen readers on its icon buttons: its role and person, or "shift". */
function shiftName(shift: Shift): string {
  return [shift.role?.name, shift.crewMember?.name].filter(Boolean).join(', ') || 'shift';
}
